import { apiSlice } from "@lxp-app/store/slices/api/apiSlice";

export const learningCanvasApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateNote: builder.mutation({
      query: ({ id, data }: any) => ({
        url: `/learning-wall/${id}`,
        method: "PUT",
        body: data
      }),
      extraOptions: { service: "Widget" }
    }),
    deleteNote: builder.mutation({
      query: ({ id }: any) => ({
        url: `/learning-wall/${id}`,
        method: "DELETE"
      }),
      async onQueryStarted(
        { id }: any,
        { dispatch, getState, queryFulfilled }
      ) {
        const patches: Array<{ undo: () => void }> = [];
        const state: any = getState();
        const apiState = state?.[apiSlice.reducerPath];
        const queries = apiState?.queries
          ? Object.values(apiState.queries)
          : [];

        queries.forEach((q: any) => {
          if (q?.endpointName !== "getAllNotes" || !q?.originalArgs) return;

          const patch = dispatch(
            (apiSlice as any).util.updateQueryData(
              "getAllNotes",
              q.originalArgs,
              (draft: any) => {
                const items = draft?.data?.items;
                if (!Array.isArray(items)) return;

                const before = items.length;
                draft.data.items = items.filter(
                  (item: any) => String(item?.id) !== String(id)
                );

                if (
                  typeof draft?.data?.totalItems === "number" &&
                  draft.data.items.length < before
                ) {
                  draft.data.totalItems = Math.max(
                    0,
                    draft.data.totalItems - 1
                  );
                }
              }
            )
          );

          patches.push(patch as { undo: () => void });
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      extraOptions: { service: "Widget" }
    }),
    publishNote: builder.mutation({
      query: ({ data }) => (
        console.log("data in api", data),
        {
          url: `/learning-wall`,
          method: "POST",
          body: data
        }
      ),
      extraOptions: { service: "Widget" }
    }),
    getAllNotes: builder.query({
      query: ({ page, limit, searchQuery, contentType }: any) => ({
        url: `/learning-wall/library?pageNumber=${page}&pageSize=${limit}&title=${searchQuery}&contentType=${contentType}`,
        method: "GET"
      }),
      // Keep one cache bucket per filter/search and append pages into it.
      serializeQueryArgs: ({ queryArgs }) => {
        const search = queryArgs?.searchQuery ?? "";
        const type = queryArgs?.contentType ?? "";
        return `getAllNotes-${search}-${type}`;
      },
      merge: (currentCache: any, newResponse: any, { arg }: any) => {
        const incomingItems = newResponse?.data?.items ?? [];

        if (!currentCache?.data) {
          return newResponse;
        }

        // For first page requests, replace to avoid stale mixed lists.
        if ((arg?.page ?? 1) <= 1) {
          currentCache.data = {
            ...newResponse.data,
            __paginationMeta: {
              lastPageItemCount: incomingItems.length,
              page: arg?.page ?? 1,
              limit: arg?.limit ?? incomingItems.length
            }
          };
          return;
        }

        const existingItems = currentCache?.data?.items ?? [];
        const byId = new Map<string, any>();

        existingItems.forEach((item: any) => {
          if (item?.id != null) {
            byId.set(String(item.id), item);
          }
        });

        incomingItems.forEach((item: any) => {
          if (item?.id != null) {
            byId.set(String(item.id), item);
          }
        });

        currentCache.data = {
          ...newResponse.data,
          items: Array.from(byId.values()),
          __paginationMeta: {
            lastPageItemCount: incomingItems.length,
            page: arg?.page ?? 1,
            limit: arg?.limit ?? incomingItems.length
          }
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.page !== previousArg?.page ||
          currentArg?.limit !== previousArg?.limit ||
          currentArg?.searchQuery !== previousArg?.searchQuery ||
          currentArg?.contentType !== previousArg?.contentType
        );
      },
      extraOptions: { service: "Widget" }
    }),
    getNoteById: builder.query({
      query: ({ id, contentType }: any) => ({
        url: `/learning-wall/library/content/${contentType}/${id}`,
        method: "GET"
      }),
      extraOptions: { service: "Widget" }
    }),
    getReactionsForNote: builder.query({
      query: ({ id }: any) => ({
        url: `/learning-wall-reactions/learning-wall/${id}`,
        method: "GET"
      }),
      extraOptions: { service: "Widget" }
    }),
    addReaction: builder.mutation({
      query: ({ id, data }: any) => ({
        url: `/learning-wall-reactions`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Widget" }
    }),
    deleteReaction: builder.mutation({
      query: ({ reactionId, noteId }: any) => ({
        url: `/learning-wall-reactions/${reactionId}`,
        method: "DELETE"
      }),
      extraOptions: { service: "Widget" }
    })
  })
});

export const {
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetAllNotesQuery,
  useGetNoteByIdQuery,
  useGetReactionsForNoteQuery,
  useLazyGetReactionsForNoteQuery,
  useAddReactionMutation,
  useDeleteReactionMutation,
  usePublishNoteMutation
} = learningCanvasApi;
